/**
 * Purpose: Selects questions from the pack based on player profile and game state.
 * Owns: Question filtering by age/tier/category, repeat avoidance, random selection.
 * Notes: Questions are NEVER generated at runtime — all come from the curated JSON
 *        pack. This module filters and picks from that pre-built pool.
 *        Updated for the classroom system: questions are selected based on the
 *        player's current class subject. During the Final Exam, questions can
 *        come from any category.
 *        Used question IDs are tracked in GameState.usedQuestionIds to prevent
 *        repeats within a single game session.
 */

import type { Question, AgeGroup, Tier, Category } from '../types/question';
import { resolveQuestionAgeGroup } from '../config/modeConfig';

// _______________________________________________
//              QUESTION FILTERING
// _______________________________________________

interface QuestionFilter {
  /** Target age group */
  ageGroup: AgeGroup;
  /** Target difficulty tier */
  tier: Tier;
  /**
   * Target subject category (based on current class period).
   * null = Final Exam, pull from any category.
   */
  category: Category | null;
  /** Question IDs already used this session (to avoid repeats) */
  usedIds: Set<string>;
}

/**
 * Filter the question pool to find candidates matching the player's profile.
 * Returns questions that match age group + tier + category and haven't been used yet.
 * If category is null (Final Exam), matches any category.
 */
export function filterQuestions(
  allQuestions: Question[],
  filter: QuestionFilter,
): Question[] {
  return allQuestions.filter(
    (q) =>
      q.ageGroup === filter.ageGroup &&
      q.tier === filter.tier &&
      (filter.category === null || q.category === filter.category) &&
      !filter.usedIds.has(q.id),
  );
}

// _______________________________________________
//              FALLBACK STRATEGY
// _______________________________________________
// If the exact match pool is empty, progressively relax constraints:
// 1. Try same age group + tier, any category
// 2. Try same age group, any tier, same category
// 3. Try same age group, any tier, any category
// 4. Try any unused question
// This ensures the game never stalls due to lack of questions.

/**
 * Find candidate questions with progressive fallback.
 * Prefers exact match, then relaxes category, then tier, then both.
 */
export function findCandidateQuestions(
  allQuestions: Question[],
  filter: QuestionFilter,
): Question[] {
  /* Exact match: age + tier + category (or any category for Final Exam) */
  let candidates = filterQuestions(allQuestions, filter);
  if (candidates.length > 0) return candidates;

  /* Fallback 1: age + tier, any category */
  candidates = allQuestions.filter(
    (q) =>
      q.ageGroup === filter.ageGroup &&
      q.tier === filter.tier &&
      !filter.usedIds.has(q.id),
  );
  if (candidates.length > 0) return candidates;

  /* Fallback 2: age + category, any tier (skip if Final Exam since category is null) */
  if (filter.category !== null) {
    candidates = allQuestions.filter(
      (q) =>
        q.ageGroup === filter.ageGroup &&
        q.category === filter.category &&
        !filter.usedIds.has(q.id),
    );
    if (candidates.length > 0) return candidates;
  }

  /* Fallback 3: same age, any tier, any category */
  candidates = allQuestions.filter(
    (q) => q.ageGroup === filter.ageGroup && !filter.usedIds.has(q.id),
  );
  if (candidates.length > 0) return candidates;

  /* Last resort: any unused question */
  candidates = allQuestions.filter((q) => !filter.usedIds.has(q.id));
  return candidates;
}

// _______________________________________________
//              RANDOM SELECTION
// _______________________________________________

/** Pick a random element from an array. Returns undefined if array is empty. */
function pickRandom<T>(items: T[]): T | undefined {
  if (items.length === 0) return undefined;
  const index = Math.floor(Math.random() * items.length);
  return items[index];
}

/**
 * Select a single question for the current player and class period.
 * Returns null only if the entire question bank has been exhausted.
 *
 * @param allQuestions - The full question pool from the pack
 * @param ageGroup - Player's age group
 * @param tier - Player's difficulty tier
 * @param category - Current class subject, or null for Final Exam (any category)
 * @param usedIds - Set of already-used question IDs
 */
export function selectQuestion(
  allQuestions: Question[],
  ageGroup: AgeGroup,
  tier: Tier,
  category: Category | null,
  usedIds: Set<string>,
): Question | null {
  /* Resolve 'parents' to 'adults-18+' for question lookup since no dedicated questions exist */
  const resolvedAgeGroup = resolveQuestionAgeGroup(ageGroup);

  const candidates = findCandidateQuestions(allQuestions, {
    ageGroup: resolvedAgeGroup,
    tier,
    category,
    usedIds,
  });

  return pickRandom(candidates) ?? null;
}
